/**
 * Router vanilla baseado em History API (URLs limpas, sem hash).
 *
 * Uso:
 *   const router = new Router({
 *     '/': () => renderDashboard(),
 *     '/propostas': () => renderListaPropostas(),
 *     '/propostas/nova': () => renderNovaProposta(),
 *     '/propostas/:id': ({ id }) => renderDetalheProposta(id),
 *   });
 *   router.start();
 *
 * Pra navegar programaticamente:
 *   router.navigate('/propostas');
 *
 * No HTML, links com `data-route` são interceptados:
 *   <a href="/propostas" data-route>Propostas</a>
 */

export type RouteParams = Record<string, string>;
export type RouteHandler = (params: RouteParams) => void | Promise<void>;
export type RouteMap = Record<string, RouteHandler>;

interface CompiledRoute {
  pattern: RegExp;
  paramNames: string[];
  handler: RouteHandler;
  template: string;
}

export class Router {
  private routes: CompiledRoute[] = [];
  private notFoundHandler: RouteHandler = () => {
    document.title = 'Não encontrado · Infomatic Propostas';
    const $app = document.getElementById('app');
    if ($app) {
      $app.innerHTML = '<main class="container"><h1>404</h1><p>Rota não encontrada.</p></main>';
    }
  };

  constructor(routes: RouteMap) {
    for (const [template, handler] of Object.entries(routes)) {
      this.routes.push(this.compileRoute(template, handler));
    }
  }

  /** Define handler customizado pra rotas não encontradas. */
  setNotFound(handler: RouteHandler): void {
    this.notFoundHandler = handler;
  }

  /** Inicia o router: escuta popstate + intercepta cliques em [data-route]. */
  start(): void {
    window.addEventListener('popstate', () => {
      void this.dispatch();
    });

    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const link = target?.closest<HTMLAnchorElement>('a[data-route]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('//')) return;
      event.preventDefault();
      this.navigate(href);
    });

    void this.dispatch();
  }

  /** Navega programaticamente (atualiza URL + dispara handler). */
  navigate(path: string): void {
    history.pushState({}, '', path);
    void this.dispatch();
  }

  /** Substitui URL atual sem push (não vai pro histórico do browser). */
  replace(path: string): void {
    history.replaceState({}, '', path);
    void this.dispatch();
  }

  private async dispatch(): Promise<void> {
    const path = window.location.pathname;
    for (const route of this.routes) {
      const match = route.pattern.exec(path);
      if (match) {
        const params: RouteParams = {};
        route.paramNames.forEach((name, i) => {
          params[name] = decodeURIComponent(match[i + 1] ?? '');
        });
        try {
          await route.handler(params);
        } catch (err) {
          console.error(`[router] Handler '${route.template}' falhou`, err);
        }
        return;
      }
    }
    await this.notFoundHandler({});
  }

  private compileRoute(template: string, handler: RouteHandler): CompiledRoute {
    const paramNames: string[] = [];
    const pattern = template.replace(/\//g, '\\/').replace(/:([\w]+)/g, (_match, name: string) => {
      paramNames.push(name);
      return '([^/]+)';
    });
    return {
      pattern: new RegExp(`^${pattern}$`),
      paramNames,
      handler,
      template,
    };
  }
}

let routerInstance: Router | null = null;

/** Retorna o router atual (se criado via initRouter). */
export function getRouter(): Router {
  if (!routerInstance) {
    throw new Error('Router não inicializado — chame initRouter() primeiro');
  }
  return routerInstance;
}

export function initRouter(routes: RouteMap): Router {
  routerInstance = new Router(routes);
  return routerInstance;
}
